import { useCategoryOptionColorsResolver } from '@/features/colors/hooks/useCategoryOptionColorsResolver';
import type { PageExperience } from '@/features/experiences/types';
import { Box, type BoxProps, CloseButton, Flex, forwardRef, Icon } from '@/ui';

type Props = {
  categories: PageExperience['categories'];
  whoLabels: PageExperience['whoLabels'];
  labels: PageExperience['labels'];
  sources: PageExperience['feeds'];
  onClick: () => void;
  onClear: () => void;
};

const CategoryDot = (props: BoxProps) => (
  <Box borderRadius="50%" flexShrink="0" h="10px" w="10px" {...props} />
);

const SummarySegment = ({
  icon,
  text,
}: {
  icon: 'User' | 'Tag' | 'Zap';
  text: string;
}) => (
  <Flex align="center" gap="2" minW="0">
    <Icon flexShrink={0} icon={icon} size={3.5} />
    <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
      {text}
    </Box>
  </Flex>
);

// Replaces the Filter trigger while filters are applied: a clickable summary of
// the active selections plus an X that clears them all (legacy Schedule parity).
export const ActiveFiltersPill = forwardRef(
  (
    { categories, whoLabels, labels, sources, onClick, onClear }: Props,
    ref
  ) => {
    const resolveOptionColors = useCategoryOptionColorsResolver();

    return (
      <Flex
        align="center"
        borderRadius="full"
        data-group
        fontSize="ah-control"
        fontWeight="medium"
        h="32px"
        maxW="420px"
        overflow="hidden"
        ref={ref}
      >
        <Flex
          _hover={{ borderColor: 'gray.400' }}
          align="center"
          borderColor="line.DEFAULT"
          borderLeftRadius="full"
          borderWidth="1px 0 1px 1px"
          gap="4"
          h="100%"
          lineHeight="normal"
          minW="0"
          overflow="hidden"
          pl="4"
          pr="3"
          role="button"
          tabIndex={0}
          onClick={onClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              // stop Space from scrolling the page
              event.preventDefault();
              onClick();
            }
          }}
        >
          {categories.length > 0 && (
            <Flex align="center" gap="2" minW="0">
              {categories.map((category) => {
                const { backgroundColor } = resolveOptionColors(
                  category.colorName
                );
                return (
                  <Flex align="center" gap="2" key={category.id} minW="0">
                    <CategoryDot bg={backgroundColor} />
                    {category.text && (
                      <Box
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {category.text}
                      </Box>
                    )}
                  </Flex>
                );
              })}
            </Flex>
          )}

          {whoLabels.length > 0 && (
            <SummarySegment
              icon="User"
              text={whoLabels.map(({ text }) => text).join(', ')}
            />
          )}

          {labels.length > 0 && (
            <SummarySegment
              icon="Tag"
              text={labels.map(({ text }) => text).join(', ')}
            />
          )}

          {sources.length > 0 && (
            <SummarySegment
              icon="Zap"
              text={sources.map(({ title }) => title).join(', ')}
            />
          )}
        </Flex>

        <Box
          _groupHover={{ bg: 'gray.400' }}
          bg="line.DEFAULT"
          h="100%"
          w="1px"
        />

        <CloseButton
          aria-label="Clear filters"
          borderColor="line.DEFAULT"
          borderLeftWidth="0"
          borderRightRadius="full"
          borderWidth="1px 1px 1px 0"
          color="ink.muted"
          h="100%"
          pl="3"
          pr="4"
          size="sm"
          _hover={{
            color: 'ink.strong',
            borderColor: 'gray.400',
          }}
          onClick={onClear}
        />
      </Flex>
    );
  }
);
